import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enHome from "./locales/en/home.json";
import enLogin from "./locales/en/login.json";
import enNotFound from "./locales/en/notFound.json";
import enServiceCard from "./locales/en/serviceCard.json";
import enLibrary from "./locales/en/library.json";
import enSongPlatforms from "./locales/en/songPlatforms.json";
import enPlaylistDetail from "./locales/en/playlistDetail.json";
import enDiscussionQuestions from "./locales/en/discussionQuestions.json";
import enLaunchpad from "./locales/en/launchpad.json";
import enEvents from "./locales/en/events.json";
import enServiceDetail from "./locales/en/serviceDetail.json";
import enAppHandoff from "./locales/en/appHandoff.json";
import enTeamInvite from "./locales/en/teamInvite.json";

import frHome from "./locales/fr/home.json";
import frLogin from "./locales/fr/login.json";
import frNotFound from "./locales/fr/notFound.json";
import frServiceCard from "./locales/fr/serviceCard.json";
import frLibrary from "./locales/fr/library.json";
import frSongPlatforms from "./locales/fr/songPlatforms.json";
import frPlaylistDetail from "./locales/fr/playlistDetail.json";
import frDiscussionQuestions from "./locales/fr/discussionQuestions.json";
import frLaunchpad from "./locales/fr/launchpad.json";
import frEvents from "./locales/fr/events.json";
import frServiceDetail from "./locales/fr/serviceDetail.json";
import frAppHandoff from "./locales/fr/appHandoff.json";
import frTeamInvite from "./locales/fr/teamInvite.json";

import frCALogin from "./locales/fr-CA/login.json";
import frCATeamInvite from "./locales/fr-CA/teamInvite.json";

// Strings are split into one file per namespace (src/i18n/locales/<lang>/<ns>.json)
// so each screen's copy lives in an obvious place.
//
// `fr` is the base French catalog and carries every namespace. `fr-CA` is a
// thin regional overlay — it ships only the files whose wording genuinely
// differs in Canadian French (currently login and teamInvite, for "ouvrir une
// session" over "se connecter" and "courriel" over "e-mail"). Anything it
// omits falls through to `fr`, then to `en`.
//
// With nonExplicitSupportedLngs, regional variants we don't ship explicitly
// (fr-FR, fr-BE, fr-CH...) resolve to `fr`, so every French speaker gets
// French rather than English.
export const resources = {
  en: { home: enHome, login: enLogin, notFound: enNotFound, serviceCard: enServiceCard, library: enLibrary, songPlatforms: enSongPlatforms, playlistDetail: enPlaylistDetail, discussionQuestions: enDiscussionQuestions, launchpad: enLaunchpad, events: enEvents, serviceDetail: enServiceDetail, appHandoff: enAppHandoff, teamInvite: enTeamInvite },
  fr: { home: frHome, login: frLogin, notFound: frNotFound, serviceCard: frServiceCard, library: frLibrary, songPlatforms: frSongPlatforms, playlistDetail: frPlaylistDetail, discussionQuestions: frDiscussionQuestions, launchpad: frLaunchpad, events: frEvents, serviceDetail: frServiceDetail, appHandoff: frAppHandoff, teamInvite: frTeamInvite },
  "fr-CA": { login: frCALogin, teamInvite: frCATeamInvite },
} as const;

export const SUPPORTED_LANGUAGES = ["en", "fr", "fr-CA"] as const;
export const NAMESPACES = ["home", "login", "notFound", "serviceCard", "library", "songPlatforms", "playlistDetail", "discussionQuestions", "launchpad", "events", "serviceDetail", "appHandoff", "teamInvite"] as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ns: NAMESPACES as unknown as string[],
    defaultNS: "home",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    fallbackLng: {
      "fr-CA": ["fr", "en"],
      default: ["en"],
    },
    // fr-CA ships only the namespaces it overrides, so a missing key must fall
    // through to fr rather than resolving to an empty string.
    fallbackNS: false,
    returnEmptyString: false,
    interpolation: {
      // React already escapes rendered values.
      escapeValue: false,
    },
    detection: {
      // ?lang=fr-CA is handy for QA and for links we send to a known audience.
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "lang",
      lookupLocalStorage: "lang",
      caches: ["localStorage"],
    },
  });

// Keep <html lang> in step with the active language for screen readers,
// browser translation prompts, and correct hyphenation.
function syncDocumentLang(lng: string) {
  document.documentElement.lang = lng;
}

syncDocumentLang(i18n.resolvedLanguage ?? "en");
i18n.on("languageChanged", syncDocumentLang);

export default i18n;
